<script lang="ts">
	import type { NewsletterBlock } from '$models/Block/NewsletterBlock';
	import ArrowRightIcon from '$components/UI/Icons/ArrowRightIcon.svelte';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { createCmsFormState } from '$lib/state/cms-form.svelte';
	import { sanitizeHtml } from '$lib/utils';

	let { block }: { block: NewsletterBlock } = $props();

	const formState = createCmsFormState();

	let emailField = $derived(block.form.fields.find((field) => field.type === 'email'));
	let emailError = $derived(emailField ? formState.error(emailField.id) : '');

	async function handleSubmit(event: SubmitEvent) {
		event.preventDefault();

		if (!emailField) {
			return;
		}

		await formState.submit(event.currentTarget as HTMLFormElement, block.errorMessage);
	}
</script>

<div class="newsletter">
	<div class="newsletter-container container">
		{#if block.image}
			<figure class="newsletter-image">
				<img
					src={block.image.url}
					srcset={getWordPressImageSrcSet(block.image)}
					sizes="120px"
					alt={block.image.alt}
					width={block.image.width}
					height={block.image.height}
					loading="lazy"
					decoding="async"
				/>
			</figure>
		{/if}

		{#if block.title}
			<h2 class="newsletter-title">{@html sanitizeHtml(block.title)}</h2>
		{/if}

		<div class="newsletter-form">
			{#if formState.success()}
				<div class="newsletter-message message message--success">
					{@html sanitizeHtml(block.form.successAction.message ?? '')}
				</div>
			{:else if emailField}
				<form
					class={['newsletter-form-inner', formState.submitting() && 'is-loading']}
					onsubmit={handleSubmit}
				>
					<div class="newsletter-field">
						<label class="screenreader" for="newsletter-email-{block.form.id}">
							{emailField.name}
						</label>
						<input
							id="newsletter-email-{block.form.id}"
							class="newsletter-input"
							type="email"
							name={emailField.id}
							placeholder={emailField.name}
							required={emailField.required}
							disabled={formState.submitting()}
						/>
						<button
							class="newsletter-submit"
							type="submit"
							aria-label={block.form.submitLabel}
							disabled={formState.submitting()}
						>
							<ArrowRightIcon />
						</button>
					</div>

					{#if emailError || formState.errorMessage()}
						<div class="newsletter-error form-error">
							{emailError || formState.errorMessage()}
						</div>
					{/if}

					<input type="hidden" name="form_id" value={block.form.id} />
				</form>
			{/if}
		</div>
	</div>
</div>

<style lang="postcss">
	.newsletter {
		&-container {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 24px;
			padding: 20px 0;
			max-width: 480px;
		}

		&-image {
			flex-shrink: 0;

			img {
				display: block;
				width: auto;
				max-width: 100px;
				height: auto;
			}
		}

		&-title {
			font-size: 24px;
			line-height: 1;
			text-align: center;
		}

		&-form {
			width: 100%;
		}

		&-form-inner {
			width: 100%;
		}

		&-field {
			position: relative;
			display: flex;
			align-items: stretch;
			width: 100%;
		}

		&-input {
			cursor: text;
			border-radius: 4px;
			padding: 19px 16px;
			transition: border-color 0.2s ease-in-out;

			&::placeholder {
				color: rgba(89, 46, 44, 0.8); /* var(--color-brown), 80% */
			}

			&:focus {
				outline: none;
				border-color: var(--color-brown);
			}
		}

		&-submit {
			position: absolute;
			display: flex;
			justify-content: center;
			align-items: center;
			right: 16px;
			top: 0;
			bottom: 0;
			width: 20px;
			border: 0;

			:global(svg path) {
				stroke: var(--color-brown);
			}

			&:disabled {
				opacity: 0.5;
			}
		}

		&-message {
			width: 100%;
			padding: 20px 16px;
			font-size: 16px;
			line-height: 1.25;
			text-align: center;
		}

		&-error {
			margin-top: var(--spacing-2);
		}
	}

	@media (--lg) {
		.newsletter {
			&-container {
				flex-direction: row;
				align-items: center;
				gap: 40px;
				max-width: none;
				padding: 0;
			}

			&-image {
				flex-shrink: 0;
			}

			&-form {
				flex: 1;
				max-width: 480px;
			}

			&-title {
				text-align: left;
				width: 50%;
			}

			&-message {
				text-align: left;
			}
		}
	}
</style>
